import { useEffect, useState, type CSSProperties, type ReactNode } from 'react';
import { Sparkles } from 'lucide-react';
import { supabase } from '@/lib/supabase';
import { useAuth } from '@/contexts/AuthContext';
import { useToast } from '@/contexts/ToastContext';
import LoadingState from '@/components/ui/LoadingState';

const PAGE_STYLE: CSSProperties = {
  flex: 1,
  background: 'var(--panel)',
  borderRadius: '20px',
  boxShadow: '0 4px 20px rgba(0, 0, 0, 0.12)',
  padding: '40px 32px',
  overflow: 'auto',
  display: 'flex',
  flexDirection: 'column',
  alignItems: 'center',
  justifyContent: 'center',
};

const CARD_STYLE: CSSProperties = {
  border: '1px solid #e5e7eb',
  borderRadius: '12px',
  padding: '32px',
  background: 'linear-gradient(135deg, #fafbff 0%, #ffffff 100%)',
  boxShadow: '0 1px 3px rgba(0, 0, 0, 0.04)',
  textAlign: 'center',
};

const ICON_BADGE_STYLE: CSSProperties = {
  display: 'inline-flex',
  alignItems: 'center',
  justifyContent: 'center',
  width: '56px',
  height: '56px',
  borderRadius: '14px',
  background: 'linear-gradient(135deg, #dbeafe 0%, #c7d2fe 100%)',
  color: '#154680',
  marginBottom: '16px',
};

const TITLE_STYLE: CSSProperties = {
  margin: 0,
  fontSize: '20px',
  fontWeight: 700,
  color: '#154680',
};

const BETA_PILL_STYLE: CSSProperties = {
  display: 'inline-block',
  marginLeft: '8px',
  padding: '2px 8px',
  fontSize: '11px',
  fontWeight: 600,
  letterSpacing: '0.4px',
  textTransform: 'uppercase',
  color: '#7c3aed',
  background: '#ede9fe',
  borderRadius: '999px',
  verticalAlign: 'middle',
};

const DESCRIPTION_STYLE: CSSProperties = {
  margin: '12px auto 24px',
  fontSize: '14px',
  lineHeight: 1.6,
  color: '#4b5563',
  maxWidth: '480px',
};

const NO_PERMISSION_STYLE: CSSProperties = {
  marginTop: '16px',
  padding: '12px 16px',
  fontSize: '13px',
  color: '#6b7280',
  background: '#f9fafb',
  borderRadius: '8px',
  border: '1px solid #e5e7eb',
};

interface ApiBetaGateProps {
  children: ReactNode;
}

export default function ApiBetaGate({ children }: ApiBetaGateProps) {
  const { tenantId, hasPermission } = useAuth();
  const { showSuccess, showError } = useToast();
  const [enabled, setEnabled] = useState<boolean | null>(null);
  const [activating, setActivating] = useState(false);

  const canActivate = hasPermission('EMPRESA', 'EDIT');

  useEffect(() => {
    if (!tenantId) {
      setEnabled(null);
      return;
    }
    let cancelled = false;
    (async () => {
      const { data, error } = await supabase
        .from('tenant')
        .select('api_beta_enabled')
        .eq('id', tenantId)
        .single();
      if (cancelled) return;
      if (error) {
        showError('Erro ao verificar acesso ao beta da API');
        setEnabled(false);
        return;
      }
      setEnabled(!!data?.api_beta_enabled);
    })();
    return () => {
      cancelled = true;
    };
  }, [tenantId, showError]);

  const handleActivate = async () => {
    if (!tenantId) return;
    setActivating(true);
    const { error } = await supabase
      .from('tenant')
      .update({ api_beta_enabled: true })
      .eq('id', tenantId);
    setActivating(false);
    if (error) {
      showError('Não foi possível ativar a API beta');
      return;
    }
    showSuccess('API beta ativada para esta empresa');
    setEnabled(true);
  };

  if (enabled === null) {
    return <LoadingState message="Verificando acesso ao beta da API..." />;
  }

  if (enabled) {
    return <>{children}</>;
  }

  return (
    <div style={PAGE_STYLE}>
      <div style={CARD_STYLE}>
        <div style={ICON_BADGE_STYLE}>
          <Sparkles size={28} strokeWidth={2} />
        </div>

        <h1 style={TITLE_STYLE}>
          API Pública do Joshua
          <span style={BETA_PILL_STYLE}>Beta</span>
        </h1>

        <p style={DESCRIPTION_STYLE}>
          Conecte ferramentas externas, MCP e automações ao Joshua através da API
          pública. Inclui endpoints REST para pessoas, obras, atividades e ordens de
          serviço, webhooks de eventos em tempo real e suporte nativo ao protocolo
          MCP para agentes de IA.
        </p>

        {canActivate ? (
          <button
            className="btn-add plus-animated"
            onClick={handleActivate}
            disabled={activating}
            style={{ opacity: activating ? 0.6 : 1, cursor: activating ? 'wait' : 'pointer' }}
          >
            <Sparkles size={14} strokeWidth={2.5} />
            <span className="btn-text">
              {activating ? 'Ativando...' : 'Ativar API (beta)'}
            </span>
          </button>
        ) : (
          <div style={NO_PERMISSION_STYLE}>
            Apenas administradores da empresa podem ativar o beta. Peça a um usuário
            com permissão de edição da empresa para ativar.
          </div>
        )}
      </div>
    </div>
  );
}
